import React from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, CopyIcon, HelpIcon, NewTabIcon, RawButton, Text } from '@common-fe/common-fe';

import { useCopyToClipboard } from '@/utils';

import { useGetHelpItems } from './hooks';

interface HelpItemProps {
  background?: string;
  largeModeIcon?: React.ReactNode;
  isLarge?: boolean;
  title: string;
  text: string;
  url: string;
}
const HelpItem: React.FC<HelpItemProps> = ({ background, title, text, url, largeModeIcon, isLarge }) => {
  return (
    <RawButton data-testId="HelpScreen_helpCenter" style={{ flex: 1 }} onClick={() => window.open(url)}>
      <Box
        direction="column"
        background={background || 'accentActive'}
        width="100%"
        round="container1Round"
        pad="spacing16"
        height={{ min: '130px' }}
        style={{ position: 'relative' }}
      >
        <NewTabIcon size="32px" color="iconOnColor" style={{ margin: -6 }} />
        <Text color="textOnColor" size="large" weight={700} margin={{ top: 'spacing12' }}>
          {title}
        </Text>
        <Text color="textOnColor" size="small" margin={{ top: 'spacing8' }}>
          {text}
        </Text>
        {isLarge && (
          <Box style={{ position: 'absolute', bottom: '-24px', right: '-42px', opacity: 0.09 }}>
            {largeModeIcon}
          </Box>
        )}
      </Box>
    </RawButton>
  );
};

const HelpMobileScreen = () => {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const { items, helpCenter } = useGetHelpItems();
  return (
    <Box data-testId="HelpScreen" direction="column" pad={{ horizontal: 'spacing16' }}>
      {helpCenter.url && (
        <Box direction="row" justify="stretch" gap="spacing12">
          <HelpItem
            url={helpCenter.url}
            isLarge
            title={helpCenter.title || t('Help Center')}
            text={t('Explore guides and troubleshooting steps')}
            largeModeIcon={<HelpIcon color="iconOnColor" size="100px" />}
          />
        </Box>
      )}
      <Box data-testId="HelpScreen_items" direction="column" margin={{ top: 'spacing16' }}>
        {items?.map((item) => (
          <Box
            data-testId={`HelpScreen_item_${item.value}`}
            direction="row"
            height="44px"
            border={{ side: 'top', color: 'border1' }}
            align="center"
            key={item.value}
          >
            {item.icon}
            <Box fill justify="center" margin={{ left: 'spacing12' }}>
              {item.url ? (
                <Anchor data-testId={`HelpScreen_item_${item.value}_link`} href={item.url} target="_blank">
                  <Text color="textBody" weight={400} size="medium">
                    {item.value}
                  </Text>
                </Anchor>
              ) : (
                <Text color="textBody" weight={400} size="medium">
                  {item.value}
                </Text>
              )}
            </Box>
            {item.isCopy && (
              <RawButton
                data-testId={`HelpScreen_item_${item.value}_copy`}
                onClick={() => copyToClipboard(item.rawUrl)}
              >
                <Box align="center" justify="center">
                  <CopyIcon size="22px" color="iconSecondary" />
                </Box>
              </RawButton>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HelpMobileScreen;
